// src/pages/RegisterPage.tsx
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// Password strength indicator
function getPasswordStrength(password: string): { strength: number; label: string; color: 'error' | 'warning' | 'info' | 'success' } {
  if (password.length === 0) {
    return { strength: 0, label: '', color: 'info' };
  }
  
  let strength = 0;
  if (password.length >= 8) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;
  
  if (strength <= 2) {
    return { strength: 20, label: 'Weak', color: 'error' };
  } else if (strength === 3) {
    return { strength: 40, label: 'Fair', color: 'warning' };
  } else if (strength === 4) {
    return { strength: 60, label: 'Good', color: 'info' };
  } else {
    return { strength: 100, label: 'Strong', color: 'success' };
  }
}

export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const { register, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [invitationToken, setInvitationToken] = useState('');
  const [invitationRoleName, setInvitationRoleName] = useState<string | null>(null);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{ strength: number; label: string; color: 'error' | 'warning' | 'info' | 'success' }>({ strength: 0, label: '', color: 'info' });
  
  // Terms and Conditions state
  const [termsContent, setTermsContent] = useState<string>('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);

  // Fetch invitation details by token to get email if not in URL
  useEffect(() => {
    const fetchInvitationEmail = async (token: string) => {
      setLoadingEmail(true);
      setInvitationError(null);
      setInvitationRoleName(null);
      
      try {
        // Try the endpoint as specified by backend: /api/auth/validate-invitation
        let response = await fetch(`${API_BASE_URL}/api/auth/validate-invitation?token=${encodeURIComponent(token)}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        // If 404, try alternative path pattern (some backends use /auth/ instead of /api/auth/)
        if (response.status === 404) {
          response = await fetch(`${API_BASE_URL}/auth/validate-invitation?token=${encodeURIComponent(token)}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });
        }

        // Handle 404 - endpoint not found (after trying both paths)
        if (response.status === 404) {
          setInvitationError('Invitation validation endpoint not found. The service may not be available yet. You can still register by entering your email manually.');
          setLoadingEmail(false);
          return;
        }

        // Handle other non-OK responses
        if (!response.ok) {
          // Try to parse error response
          try {
            const errorData = await response.json();
            const errorMessage = errorData.message || `Server error: ${response.status} ${response.statusText}`;
            setInvitationError(errorMessage);
          } catch {
            // If we can't parse the error, show generic message
            setInvitationError(`Could not validate invitation (${response.status}). You can still register manually.`);
          }
          setLoadingEmail(false);
          return;
        }

        // Response is OK - parse JSON
        const data = await response.json();

        // Backend always provides email (even for invalid invitations) for display purposes
        if (data.email) {
          setEmail(data.email);
        }

        // Check if invitation is valid
        if (data.isValid === true) {
          // Valid invitation - store role name if available
          if (data.roleName) {
            setInvitationRoleName(data.roleName);
          }
          setInvitationError(null);
        } else {
          // Invalid, expired, or already-used invitation
          // Backend provides error message and code
          const errorMessage = data.message || 'This invitation is no longer valid';
          setInvitationError(errorMessage);
          
          // Still allow registration attempt (backend will reject if truly invalid)
          // But show warning to user
        }
      } catch (err) {
        // Network or other error - allow manual entry
        console.warn('Could not validate invitation token:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
          setInvitationError('Could not connect to the server. Please check your connection and try again.');
        } else {
          setInvitationError('Could not validate invitation. Please check the link and try again.');
        }
      } finally {
        setLoadingEmail(false);
      }
    };

    // Get invitation token from URL query parameter
    const token = searchParams.get('token');
    if (token) {
      setInvitationToken(token);
    }
    
    // Get email from URL query parameter if available
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    } else if (token) {
      // If no email in URL but we have a token, try to fetch it from backend
      fetchInvitationEmail(token);
    }
  }, [searchParams]);

  useEffect(() => {
    setPasswordStrength(getPasswordStrength(password));
  }, [password]);

  // Fetch Terms and Conditions on page load
  useEffect(() => {
    const fetchTerms = async () => {
      setLoadingTerms(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/terms`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          // Handle both response formats: { terms: "..." } or direct string
          const termsText = typeof data === 'string' 
            ? data 
            : (data.terms || data.content || '');
          setTermsContent(termsText);
        } else {
          // Set a default message if API fails
          setTermsContent('Terms and Conditions could not be loaded. Please contact support.');
        }
      } catch {
        // Set a default message if API fails
        setTermsContent('Terms and Conditions could not be loaded. Please contact support.');
      } finally {
        setLoadingTerms(false);
      }
    };

    fetchTerms();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!invitationToken) {
      setError('Invitation token is required. Please use the link from your invitation email.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    // Validate Terms acceptance
    if (!termsAccepted) {
      setError('You must accept the Terms and Conditions to register.');
      return;
    }

    setLoading(true);

    try {
      await register({
        email,
        password,
        confirmPassword,
        invitationToken,
        displayName: displayName || undefined,
        termsAccepted: true,
      });
      // Navigation is handled by AuthContext after successful registration
    } catch (err: any) {
      const errorMessage = err?.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Create Account
          </Typography>
          
          {(error || authError) && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error || authError}
            </Alert>
          )}

          {invitationError && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              {invitationError}
              {invitationToken && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  You can still attempt to register, but the invitation may have expired or already been used.
                </Typography>
              )}
            </Alert>
          )}

          {invitationRoleName && !invitationError && (
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              You are registering as: <strong>{invitationRoleName}</strong>
            </Alert>
          )}

          {!invitationToken && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              Invitation token is required. Please use the link from your invitation email.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || loadingEmail}
              helperText={
                loadingEmail 
                  ? 'Validating invitation and loading email...' 
                  : invitationToken && !email 
                    ? 'Please enter the email address you were invited with' 
                    : invitationRoleName 
                      ? `Email for ${invitationRoleName} role` 
                      : ''
              }
            />
            <TextField
              margin="normal"
              fullWidth
              id="displayName"
              label="Display Name (Optional)"
              name="displayName"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              helperText="Password must be at least 8 characters with uppercase, lowercase, number, and special character"
            />
            {password && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength.strength}
                    color={passwordStrength.color}
                    sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {passwordStrength.label}
                  </Typography>
                </Box>
              </Box>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              error={confirmPassword !== '' && password !== confirmPassword}
              helperText={confirmPassword !== '' && password !== confirmPassword ? 'Passwords do not match' : ''}
            />
            <Box sx={{ mt: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    required
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="body2">
                    I accept the{' '}
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowTermsDialog(true)}
                      sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                      disabled={loading}
                    >
                      Terms and Conditions
                    </Button>
                  </Typography>
                }
              />
              {!termsAccepted && error && error.includes('Terms') && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  You must accept the Terms and Conditions to register
                </Alert>
              )}
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !invitationToken || !termsAccepted}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link to="/login" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Typography variant="body2" color="primary">
                  Already have an account? Sign in
                </Typography>
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
      
      {/* Terms and Conditions Dialog */}
      <Dialog
        open={showTermsDialog}
        onClose={() => setShowTermsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Terms and Conditions</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              maxHeight: '60vh',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              p: 2,
            }}
          >
            {loadingTerms ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Typography variant="body2" component="pre" sx={{ fontFamily: 'inherit', whiteSpace: 'pre-wrap' }}>
                {termsContent || 'Loading Terms and Conditions...'}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTermsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default RegisterPage;
